import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { STATUS_MAP, isOverdue, todayStr, toISODate } from "@/lib/taskConstants";
import { Button } from "@/components/ui/button";

const WEEKDAYS = ["L", "M", "X", "J", "V", "S", "D"];
const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

export const CalendarView = ({ tasks, onSelect }) => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());

  const navigate = (delta) => {
    const d = new Date(year, month + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth());
  };

  const firstDay = new Date(year, month, 1);
  const startOffset = (firstDay.getDay() + 6) % 7;
  const gridStart = new Date(year, month, 1 - startOffset);
  const cells = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    return d;
  });

  const scheduled = tasks.filter((t) => t.start_date || t.due_date);
  const unscheduled = tasks.length - scheduled.length;
  const today = todayStr();

  const tasksForDay = (iso) =>
    scheduled.filter((t) => {
      const start = t.start_date || t.due_date;
      const end = t.due_date || t.start_date;
      return iso >= start && iso <= end;
    });

  return (
    <div className="space-y-3" data-testid="calendar-view">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" className="h-8 w-8 p-0" data-testid="calendar-prev-btn" onClick={() => navigate(-1)}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="sm" className="h-8 w-8 p-0" data-testid="calendar-next-btn" onClick={() => navigate(1)}>
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" className="h-8 text-xs" data-testid="calendar-today-btn"
          onClick={() => { setYear(now.getFullYear()); setMonth(now.getMonth()); }}>
          Hoy
        </Button>
        <h3 className="font-heading font-semibold text-lg ml-2" data-testid="calendar-month-label">
          {MONTHS[month]} {year}
        </h3>
        {unscheduled > 0 && (
          <span className="ml-auto text-xs text-muted-foreground">{unscheduled} sin fecha</span>
        )}
      </div>

      <div className="rounded-xl border bg-card overflow-hidden">
        <div className="grid grid-cols-7 border-b bg-secondary/40">
          {WEEKDAYS.map((d) => (
            <div key={d} className="py-2 text-center text-xs font-semibold text-muted-foreground">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((date, i) => {
            const iso = toISODate(date);
            const inMonth = date.getMonth() === month;
            const isToday = iso === today;
            const dayTasks = tasksForDay(iso);
            return (
              <div key={iso} data-testid={`calendar-cell-${iso}`}
                className={`min-h-[92px] border-b border-r p-1.5 ${inMonth ? "" : "bg-secondary/20"} ${(i + 1) % 7 === 0 ? "border-r-0" : ""}`}>
                <span className={`text-xs inline-flex items-center justify-center h-5 w-5 rounded-full ${
                  isToday ? "bg-primary text-primary-foreground font-semibold" : inMonth ? "text-foreground" : "text-muted-foreground/50"
                }`}>
                  {date.getDate()}
                </span>
                <div className="space-y-1 mt-1">
                  {dayTasks.slice(0, 3).map((t) => (
                    <button key={t.id} onClick={() => onSelect(t.id)}
                      data-testid={`calendar-task-${t.id}-${iso}`}
                      className="w-full text-left text-[10px] leading-tight px-1.5 py-0.5 rounded truncate block text-white font-medium hover:opacity-80 transition-opacity"
                      style={{ backgroundColor: isOverdue(t) ? "#D93025" : STATUS_MAP[t.status]?.color }}>
                      {t.title}
                    </button>
                  ))}
                  {dayTasks.length > 3 && (
                    <span className="text-[10px] text-muted-foreground px-1">+{dayTasks.length - 3} más</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
